import { useCallback, useEffect, useState } from "react";
import {
  HourglassIcon,
  InboxIcon,
  MoreVerticalIcon,
  SendIcon,
  Trash2Icon,
  TriangleAlertIcon,
  UserPlusIcon,
} from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConfirmActionDialog } from "@/components/ConfirmActionDialog";
import { CreateClientInviteDialog } from "@/components/CreateClientInviteDialog";
import { InviteRevealDialog } from "@/components/InviteRevealDialog";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

type MemberResponse = components["schemas"]["MemberResponse"];
type ClientInviteResponse = components["schemas"]["ClientInviteResponse"];
type ClientInviteWithTokenResponse =
  components["schemas"]["ClientInviteWithTokenResponse"];
type ClientRole = MemberResponse["role"];

const ASSIGNABLE_ROLES: ClientRole[] = ["Admin", "Developer", "Member"];

function roleBadgeClass(role: ClientRole): string {
  switch (role) {
    case "Owner":
      return "border-secondary/50 bg-secondary/15 text-secondary-foreground";
    default:
      return "border-border bg-muted";
  }
}

interface ClientMembersTableProps {
  clientId: string;
}

export function ClientMembersTable({ clientId }: ClientMembersTableProps) {
  const [members, setMembers] = useState<MemberResponse[] | null>(null);
  const [invites, setInvites] = useState<ClientInviteResponse[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [revealedInvite, setRevealedInvite] =
    useState<ClientInviteWithTokenResponse | null>(null);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.GET("/v1/client/members"),
      api.GET("/v1/client/invites"),
    ])
      .then(([m, i]) => {
        if (cancelled) return;
        if (m.error || i.error) {
          setError("Couldn't load members or invites.");
          return;
        }
        setMembers(m.data ?? []);
        setInvites(i.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setError("Network error.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, tick]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  const memberCount = members?.length ?? 0;
  const pendingInvites = invites?.filter((i) => i.status === "Pending") ?? [];
  const expiredInvites = invites?.filter((i) => i.status === "Expired") ?? [];
  const pendingCount = pendingInvites.length;
  const expiredCount = expiredInvites.length;

  const revealInvite = useCallback(
    (invite: ClientInviteWithTokenResponse) => {
      setRevealedInvite(invite);
      refresh();
    },
    [refresh],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          {loading
            ? "Loading…"
            : `${memberCount} active · ${pendingCount} pending` +
              (expiredCount > 0 ? ` · ${expiredCount} expired` : "")}
        </p>
        <div className="flex items-center gap-2">
          {loading && <Spinner className="size-5" />}
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlusIcon className="size-4" />
            Invite member
          </Button>
        </div>
      </div>

      <Section title="Active members" count={memberCount}>
        {!loading && memberCount === 0 ? (
          <Empty>No members yet — invite someone to get started.</Empty>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {members?.map((m) => (
                  <MemberRow
                    key={m.userId}
                    clientId={clientId}
                    member={m}
                    onChanged={refresh}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      <Section title="Pending invites" count={pendingCount}>
        {!loading && pendingCount === 0 ? (
          <Empty>No pending invites.</Empty>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingInvites.map((i) => (
                  <InviteRow
                    key={i.id}
                    clientId={clientId}
                    invite={i}
                    onChanged={refresh}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      {expiredCount > 0 && (
        <Section title="Expired invites" count={expiredCount}>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Expired</TableHead>
                  <TableHead className="w-[140px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {expiredInvites.map((i) => (
                  <InviteRow
                    key={i.id}
                    clientId={clientId}
                    invite={i}
                    onChanged={refresh}
                    onResent={revealInvite}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </Section>
      )}

      <CreateClientInviteDialog
        clientId={clientId}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onCreated={revealInvite}
      />

      <InviteRevealDialog
        invite={revealedInvite}
        open={revealedInvite !== null}
        onOpenChange={(open) => {
          if (!open) setRevealedInvite(null);
        }}
      />
    </div>
  );
}

function MemberRow({
  member,
  clientId,
  onChanged,
}: {
  member: MemberResponse;
  clientId: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function changeRole(newRole: ClientRole) {
    setBusy(true);
    await api.PATCH("/v1/client/members/{userId}", {
      params: { path: { userId: member.userId } },
      body: { role: newRole },
    });
    setBusy(false);
    onChanged();
  }

  async function remove() {
    setBusy(true);
    await api.DELETE("/v1/client/members/{userId}", {
      params: { path: { userId: member.userId } },
    });
    setBusy(false);
    setConfirmRemove(false);
    onChanged();
  }

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex flex-col">
            <span className="font-medium">{member.name ?? member.email}</span>
            {member.name && (
              <span className="text-muted-foreground text-xs">{member.email}</span>
            )}
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={cn(roleBadgeClass(member.role))}>
            {member.role}
          </Badge>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {formatDateTime(member.joinedAt)}
        </TableCell>
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" disabled={busy}>
                {busy ? (
                  <Spinner className="size-3.5" />
                ) : (
                  <MoreVerticalIcon className="size-3.5" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {ASSIGNABLE_ROLES.filter((r) => r !== member.role).map((r) => (
                <DropdownMenuItem key={r} onClick={() => changeRole(r)}>
                  Change to {r}
                </DropdownMenuItem>
              ))}
              {member.role !== "Owner" && (
                <DropdownMenuItem onClick={() => changeRole("Owner")}>
                  Promote to Owner
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => setConfirmRemove(true)}
                className="text-destructive"
              >
                <Trash2Icon className="size-3.5" />
                Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>

      <ConfirmActionDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title="Remove this member?"
        description={
          <>
            <span className="font-medium">{member.email}</span> will lose portal
            access for this client. Their API keys remain untouched.
          </>
        }
        confirmLabel="Remove"
        pending={busy}
        onConfirm={remove}
      />
    </>
  );
}

function InviteRow({
  invite,
  clientId,
  onChanged,
  onResent,
}: {
  invite: ClientInviteResponse;
  clientId: string;
  onChanged: () => void;
  onResent?: (invite: ClientInviteWithTokenResponse) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const expired = invite.status === "Expired";

  async function revoke() {
    setBusy(true);
    await api.DELETE("/v1/client/invites/{inviteId}", {
      params: { path: { inviteId: invite.id } },
    });
    setBusy(false);
    setConfirmRevoke(false);
    onChanged();
  }

  // The backend replaces the expired invite in place and mints a fresh token, revealed like a new invite.
  async function resend() {
    setBusy(true);
    const { data } = await api.POST("/v1/client/invites", {
      body: { email: invite.email, role: invite.role },
    });
    setBusy(false);
    if (data) onResent?.(data);
    onChanged();
  }

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-2">
            {expired ? (
              <TriangleAlertIcon className="text-destructive size-3.5" />
            ) : (
              <HourglassIcon className="text-muted-foreground size-3.5" />
            )}
            <span className="font-medium">{invite.email}</span>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={cn(roleBadgeClass(invite.role))}>
            {invite.role}
          </Badge>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {formatDateTime(invite.createdAt)}
        </TableCell>
        <TableCell className={cn(expired ? "text-destructive" : "text-muted-foreground")}>
          {formatDateTime(invite.expiresAt)}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1">
            {expired && (
              <Button
                variant="outline"
                size="sm"
                onClick={resend}
                disabled={busy}
                title="Send a fresh invite link to this address"
              >
                {busy ? (
                  <Spinner className="size-3.5" />
                ) : (
                  <SendIcon className="size-3.5" />
                )}
                Resend
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setConfirmRevoke(true)}
              disabled={busy}
              className="text-destructive"
              title="Revoke invite"
            >
              {busy && !expired ? (
                <Spinner className="size-3.5" />
              ) : (
                <Trash2Icon className="size-3.5" />
              )}
            </Button>
          </div>
        </TableCell>
      </TableRow>

      <ConfirmActionDialog
        open={confirmRevoke}
        onOpenChange={setConfirmRevoke}
        title="Revoke this invite?"
        description={
          <>
            {expired ? (
              <>
                The expired invite for{" "}
                <span className="font-medium">{invite.email}</span> will be removed
                from the list.
              </>
            ) : (
              <>
                The invite link sent to{" "}
                <span className="font-medium">{invite.email}</span> will stop working.
              </>
            )}
          </>
        }
        confirmLabel="Revoke"
        pending={busy}
        onConfirm={revoke}
      />
    </>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-2">
        <h3 className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">
          {title}
        </h3>
        <span className="text-muted-foreground text-xs tabular-nums">{count}</span>
      </div>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-border text-muted-foreground flex flex-col items-center justify-center gap-2 rounded-md border border-dashed py-10 text-center text-sm">
      <InboxIcon className="size-6" />
      {children}
    </div>
  );
}
